import { all, takeLatest, put, call, fork, select } from "redux-saga/effects";
import { actions, constants } from "./actions";
import { apiV2 } from "../../services/api";
import { canAnswerLesson } from "./reducer";

const {
  SUBMIT_LESSON_FORM,
  FETCH_LESSON_LIST,
  DELETE_LESSON,
  UPDATE_LESSON_RESPONSE,
} = constants;
const {
  submitLessonForm,
  fetchLessonList,
  deleteLesson,
  updateLesson,
  fetchLesson,
  updateLessonResponse,
  fetchLessonResponse,
} = actions;

// Action sagas - async
function* submitLessonFormRequest() {
  yield takeLatest("SUBMIT_LESSON_FORM/REQUEST", function* ({ payload }) {
    try {
      const { title, description, video_url, document_url, questions } = payload;
      yield call(apiV2.lessons.create, {
        title,
        description,
        videoUrl: video_url,
        documentUrl: document_url,
        questions,
      });
      yield put(
        submitLessonForm.success({
          source: SUBMIT_LESSON_FORM,
          message: "Lesson successfully created.",
        })
      );
      window.location.href = "/admin/professional-development";
    } catch (err) {
      yield put(
        submitLessonForm.failure({
          source: SUBMIT_LESSON_FORM,
          message: "Unable to create lesson, please try again.",
        })
      );
    }
  });
}

function* deleteLessonRequest() {
  yield takeLatest("DELETE_LESSON/REQUEST", function* ({ payload }) {
    try {
      yield call(apiV2.lessons.delete, payload.id);
      window.location.href = "/admin/professional-development";
    } catch (err) {
      yield put(
        deleteLesson.failure({
          source: DELETE_LESSON,
          message: "Unable to delete lesson, please try again.",
        })
      );
    }
  });
}

function* updateLessonRequest() {
  yield takeLatest("UPDATE_LESSON/REQUEST", function* ({ payload }) {
    try {
      const { id, title, description, video_url, videoUrl, document_url, documentUrl, questions } = payload;
      yield call(apiV2.lessons.update, id, {
        title,
        description,
        videoUrl: video_url ?? videoUrl,
        documentUrl: document_url ?? documentUrl,
        questions,
      });
      yield put(
        updateLesson.success({ message: "Lesson successfully updated." })
      );
    } catch (err) {
      yield put(
        updateLesson.failure({
          message: "Unable to update lesson, please try again.",
        })
      );
    }
  });
}

function* fetchLessonListRequest() {
  yield takeLatest("FETCH_LESSON_LIST/REQUEST", function* ({ payload }) {
    try {
      const lessons = yield call(apiV2.lessons.getAll, payload.call_taker_id);
      yield put(
        fetchLessonList.success({
          source: FETCH_LESSON_LIST,
          list: lessons,
        })
      );
    } catch (err) {
      yield put(
        fetchLessonList.failure({
          source: FETCH_LESSON_LIST,
          message: "Unable to load lessons, please try again.",
        })
      );
    }
  });
}

function* fetchLessonRequest() {
  yield takeLatest("FETCH_LESSON/REQUEST", function* ({ payload }) {
    try {
      const lesson = yield call(apiV2.lessons.getById, payload.id);
      yield put(
        fetchLesson.success({
          lesson,
        })
      );
    } catch (err) {
      yield put(
        fetchLesson.failure({
          message: "Unable to fetch lesson, please try again.",
        })
      );
    }
  });
}

function* fetchLessonResponseRequest() {
  yield takeLatest("FETCH_LESSON_RESPONSE/REQUEST", function* ({ payload }) {
    if (!payload.call_taker_id) {
      return;
    }

    try {
      const lessonResponse = yield call(
        apiV2.lessonResponses.get,
        payload.lesson,
        payload.call_taker_id
      );
      yield put(
        fetchLessonResponse.success({
          lesson: payload.lesson,
          lessonResponse: lessonResponse ?? { responses: [] },
        })
      );
    } catch (err) {
      yield put(
        fetchLessonResponse.failure({
          lesson: payload.lesson,
          message: "Unable to fetch lesson response, please try again.",
        })
      );
    }
  });
}

function* updateLessonResponseRequest() {
  yield takeLatest("UPDATE_LESSON_RESPONSE/REQUEST", function* ({ payload }) {
    // Only save answers that were loaded for this lesson. Otherwise `id` could
    // be another lesson's row, and the backend updates rows by id alone.
    const canAnswer = yield select((state) =>
      canAnswerLesson(state.lessons, payload.id)
    );
    if (!canAnswer) {
      return;
    }

    try {
      const response = yield select((state) => state.lessons.lessonResponse);
      const lessonResponse = yield call(apiV2.lessonResponses.save, {
        id: response.id || undefined,
        responses: response.responses,
        lesson: payload.id,
        callTakerId: payload.call_taker_id,
      });
      yield put(
        updateLessonResponse.success({
          lesson: payload.id,
          lessonResponse,
          source: UPDATE_LESSON_RESPONSE,
          message: "Lesson response successfully created.",
        })
      );
    } catch (err) {
      yield put(
        updateLessonResponse.failure({
          source: UPDATE_LESSON_RESPONSE,
          message: "Unable to create lesson response, please try again.",
        })
      );
    }
  });
}

// Watcher sagas
export default function* rootSaga() {
  yield all([
    fork(submitLessonFormRequest),
    fork(fetchLessonListRequest),
    fork(deleteLessonRequest),
    fork(updateLessonRequest),
    fork(fetchLessonRequest),
    fork(updateLessonResponseRequest),
    fork(fetchLessonResponseRequest),
  ]);
}
